import type { SchemeSlots } from "@shared/types/foyer";
import type { TvRoomOffer, TvRoomOfferFeed } from "@shared/types/roomOffer";

/**
 * A shop for the design preview (`?scheme=<key>&on=offers&screen=offers`).
 *
 * The Offers screen is empty until a hotel authors a catalogue, so without this
 * there is nothing to review: the one surface whose whole point is how it LOOKS
 * could only be judged after seeding a real premise's database. Exactly the
 * reasoning behind `CUSTOM_PREVIEW` in `App.tsx`, and the same guard rail -
 * this is passed from the `?scheme=` branch alone, so no bound television and no
 * real room can be made to show invented merchandise.
 *
 * One offer per kind, because the three kinds are what the card rail has to hold
 * up under: a priced order with a menu behind it, a booking with hours and a
 * capacity, and a promotion with nothing behind it at all.
 *
 * Every line carries an `icon` KEY from `@shared/offerIcons` — never a URL and
 * never an emoji — because POS-7's picker is an icon grid and a preview whose
 * tiles all fell back to the generic tag would be reviewing the fallback.
 */

const OFFERS: TvRoomOffer[] = [
  {
    id: "demo-room-service",
    scope: "premise",
    premiseId: "demo",
    companyId: null,
    title: { en: "Room service", sv: "Roomservice" },
    body: {
      en: "The kitchen is open until midnight. Order to your room and we will bring it up.",
      sv: "Köket är öppet till midnatt. Beställ till rummet så bär vi upp det.",
    },
    imageUrl:
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1280&q=70",
    category: "Dining",
    action: { kind: "order", fulfil: "deliver" },
    items: [
      {
        id: "club",
        label: { en: "Club sandwich", sv: "Clubsmörgås" },
        icon: "food",
        price: { amount: 185, currency: "kr" },
        enabled: true,
        order: 0,
      },
      {
        id: "burger",
        label: { en: "House burger", sv: "Husets burgare" },
        icon: "food",
        price: { amount: 245, currency: "kr" },
        enabled: true,
        order: 1,
      },
      {
        id: "soup",
        label: { en: "Soup of the day", sv: "Dagens soppa" },
        icon: "food",
        price: { amount: 129, currency: "kr" },
        enabled: true,
        order: 2,
      },
      {
        id: "still-water",
        label: { en: "Still water", sv: "Stilla vatten" },
        icon: "water",
        enabled: true,
        order: 3,
      },
    ],
    availability: {},
    enabled: true,
    order: 0,
    createdAt: "2026-08-01T09:00:00.000Z",
    updatedAt: "2026-08-01T09:00:00.000Z",
    slots: [],
  },
  {
    id: "demo-spa",
    scope: "premise",
    premiseId: "demo",
    companyId: null,
    title: { en: "Spa evening", sv: "Spakväll" },
    body: {
      en: "Two hours in the baths and the sauna, towels and a robe included.",
      sv: "Två timmar i baden och bastun, handdukar och badrock ingår.",
    },
    imageUrl:
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1280&q=70",
    category: "Spa",
    terms: {
      en: "Cancel up to four hours before. Guests under 16 in the morning hours only.",
      sv: "Avbokas senast fyra timmar innan. Gäster under 16 år endast förmiddagstid.",
    },
    action: {
      kind: "booking",
      price: { amount: 450, currency: "kr" },
      slots: {
        mode: "slots",
        days: [],
        startTime: "17:00",
        endTime: "21:00",
        intervalMinutes: 60,
        capacity: 4,
        horizonDays: 3,
      },
    },
    items: [],
    availability: {},
    enabled: true,
    order: 1,
    createdAt: "2026-08-01T09:00:00.000Z",
    updatedAt: "2026-08-01T09:00:00.000Z",
    // Written out rather than expanded from the policy, so the preview looks
    // the same on every machine and on every day of the week.
    slots: [
      {
        start: "2026-08-20T17:00",
        date: "2026-08-20",
        time: "17:00",
        day: "thu",
        capacity: 4,
        remaining: 4,
      },
      {
        start: "2026-08-20T18:00",
        date: "2026-08-20",
        time: "18:00",
        day: "thu",
        capacity: 4,
        remaining: 1,
      },
      {
        start: "2026-08-20T19:00",
        date: "2026-08-20",
        time: "19:00",
        day: "thu",
        capacity: 4,
        remaining: 2,
      },
      {
        start: "2026-08-20T20:00",
        date: "2026-08-20",
        time: "20:00",
        day: "thu",
        capacity: 4,
        remaining: 0,
      },
      {
        start: "2026-08-21T17:00",
        date: "2026-08-21",
        time: "17:00",
        day: "fri",
        capacity: 4,
        remaining: 4,
      },
      {
        start: "2026-08-21T18:00",
        date: "2026-08-21",
        time: "18:00",
        day: "fri",
        capacity: 4,
        remaining: 3,
      },
      {
        start: "2026-08-21T19:00",
        date: "2026-08-21",
        time: "19:00",
        day: "fri",
        capacity: 4,
        remaining: 4,
      },
    ],
  },
  {
    id: "demo-housekeeping",
    scope: "premise",
    premiseId: "demo",
    companyId: null,
    title: { en: "Ask housekeeping", sv: "Be om städservice" },
    body: {
      en: "Fresh towels, more coffee, an extra pillow. Someone will bring it up.",
      sv: "Rena handdukar, mer kaffe, en extra kudde. Någon kommer upp med det.",
    },
    category: "Housekeeping",
    action: { kind: "booking", slots: { mode: "asap" } },
    items: [
      {
        id: "towels",
        label: { en: "Fresh towels", sv: "Rena handdukar" },
        icon: "towels",
        enabled: true,
        order: 0,
      },
      {
        id: "coffee",
        label: { en: "Coffee & tea refill", sv: "Påfyllning kaffe & te" },
        icon: "coffee",
        enabled: true,
        order: 1,
      },
      {
        id: "pillow",
        label: { en: "Extra pillow", sv: "Extra kudde" },
        icon: "bedding",
        enabled: true,
        order: 2,
      },
    ],
    availability: {},
    enabled: true,
    order: 2,
    createdAt: "2026-08-01T09:00:00.000Z",
    updatedAt: "2026-08-01T09:00:00.000Z",
    slots: [],
  },
  {
    id: "demo-late-checkout",
    scope: "premise",
    premiseId: "demo",
    companyId: null,
    title: { en: "Late checkout", sv: "Sen utcheckning" },
    body: {
      en: "Keep the room until 16:00 and take the afternoon slowly.",
      sv: "Behåll rummet till 16:00 och ta eftermiddagen lugnt.",
    },
    category: "Stay",
    action: {
      kind: "order",
      price: { amount: 395, currency: "kr" },
      fulfil: "desk",
    },
    items: [],
    availability: {},
    enabled: true,
    order: 3,
    createdAt: "2026-08-01T09:00:00.000Z",
    updatedAt: "2026-08-01T09:00:00.000Z",
    slots: [],
  },
  {
    id: "demo-autumn",
    scope: "company",
    premiseId: null,
    companyId: "demo-chain",
    title: { en: "Autumn by the water", sv: "Höst vid vattnet" },
    body: {
      en: "Two nights, dinner and the baths, from October. Ask at reception.",
      sv: "Två nätter, middag och baden, från oktober. Fråga i receptionen.",
    },
    imageUrl:
      "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1280&q=70",
    category: "Offer",
    action: { kind: "promotion" },
    items: [],
    availability: {},
    enabled: true,
    order: 4,
    createdAt: "2026-08-01T09:00:00.000Z",
    updatedAt: "2026-08-01T09:00:00.000Z",
    slots: [],
  },
];

export const DEMO_OFFER_FEED: TvRoomOfferFeed = {
  offers: OFFERS,
  now: "2026-08-20T15:00:00.000Z",
  timezone: "Europe/Stockholm",
};

/**
 * The home-screen banner, for the design preview
 * (`?scheme=<key>&on=offers,promotions`).
 *
 * Both switches are needed and that is the product, not an accident of the
 * preview: `showOffers` is what makes the shop exist and `showPromotions` is
 * what puts one of its offers on the home screen (`POS-4`).
 *
 * The headline carries `{kvar}` rather than a typed number, which is the whole
 * point of the token - the preview reads "bara 4 platser kvar" because four is
 * what the demo spa evening actually has left at its next hour, and it would
 * read differently the moment that changed.
 */
export const DEMO_OFFER_PROMO_SLOT: SchemeSlots = {
  homePromo: {
    type: "offer",
    offerId: "demo-spa",
    headline: {
      sv: "Spakväll i kväll - bara {kvar} platser kvar!",
      en: "Spa evening tonight - only {kvar} places left!",
    },
  },
};
